import { useState, useEffect } from "react";
import {read_cookie} from 'sfcookies';
import { hostUrl, UseUrlManagerCleanUrl } from "../../../utils/url-manager";
import { useNotify } from "react-admin";
import { TokenHistoryGraphData, TokenHistoryTimingData, TokenCostData } from "../../../../types/dashboard";

// The API pages the timing endpoint 5 rows at a time
// (TokenHistoryRepository::getTodayTokenTimingData). The pager maths on this side has to agree
// with that number, so it is declared once here rather than re-derived per call site.
export const TIMINGS_PAGE_SIZE: number = 5;

/**
 * The four ways a dashboard request can end. Modelled as a union so each hook has to handle every
 * outcome explicitly — the previous shape let a failure fall through with `loading` already false
 * and no data, which is what blanked the dashboard.
 */
export type AuthedJsonResult<T> =
    | { status: 'success', data: T }
    | { status: 'unauthenticated' }
    | { status: 'api-error', message: string }
    | { status: 'network-error' };

/**
 * Both dashboard endpoints share the same request shape: clean the URL, read the auth cookie,
 * send it as a `token` header, and unwrap the API's { message, data } envelope. That was
 * duplicated verbatim in each hook, so a fix to one silently missed the other.
 *
 * The missing-token case never reaches the network: it can only 401, and turning the API's
 * "No Valid Token Found" body into a toast would step on the authProvider's redirect.
 */
export const fetchAuthedJson = async <T>(path: string): Promise<AuthedJsonResult<T>> => {
        const url: string = UseUrlManagerCleanUrl({ url: hostUrl + path });

        const token: string = read_cookie('auth');
        if (typeof token !== 'string' || token.length === 0) {
            return { status: 'unauthenticated' };
        }

        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token
            })
        });

        try {
            const response: Response = await fetch(request);
            const body: { message?: string, data?: T } = await response.json();
            if (body !== undefined && body.message === 'success') {
                return { status: 'success', data: body.data as T };
            }
            return { status: 'api-error', message: body?.message ?? 'Something has gone wrong!' };
        } catch (err) {
            // A rejected fetch used to leave `loading` true forever, so a dropped request pinned
            // the loading text on the card until a full page reload.
            console.error(err);
            return { status: 'network-error' };
        }
    }

export const useTokenHistoryGraphData = () => {
    // null, not [] — the payload is an object, and typing it as one is what makes the consumer
    // reach for `data?.totalTokens` instead of indexing blindly into an array that never had
    // those keys.
    const [data, setData] = useState<TokenHistoryGraphData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    // Every failure path has to land somewhere the component can see. Without this, a failed load
    // left `data` as [] with loading already false, and the render walked straight into
    // formatting undefined -> TypeError -> blank dashboard.
    const [error, setError] = useState<string | null>(null);
    const notify = useNotify();

    function loadData(): void {
        fetchAuthedJson<TokenHistoryGraphData>('hq/token-history-graph/data')
            .then((result: AuthedJsonResult<TokenHistoryGraphData>) => {
                setLoading(false);
                switch (result.status) {
                    case 'success':
                        setError(null);
                        setData(result.data);
                        break;
                    case 'unauthenticated':
                        setError('Not signed in.');
                        break;
                    case 'network-error':
                        setError('Could not reach the server.');
                        break;
                    case 'api-error':
                        setError(result.message);
                        notify(result.message, { type: 'error' });
                        break;
                }
            });
    }

    const reloadData = () => {
        // Was setLoading(false) — the refresh button gave no feedback at all because the card
        // never re-entered its loading state.
        setLoading(true);
        setError(null);
        loadData();
        return true;
    }

    useEffect( () => {
        loadData();
    }, [] );

    return { data, loading, error, reloadData };
}

export const useTokenHistoryTimingData = () => {
    const [data, setData] = useState<TokenHistoryTimingData | null>(null);
    // Starts true: the effect below sets it immediately, and starting false painted an empty
    // table for one frame before the spinner appeared.
    const [loading, setLoading] = useState<boolean>(true);
    const [pageNum, setPageNum] = useState<number>(1);
    // Defaults to 0 rather than undefined so the pager maths can never produce NaN on a click
    // that lands before the first response.
    const [totalItems, setTotalItems] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);
    const notify = useNotify();

    function loadData(): void {
        fetchAuthedJson<TokenHistoryTimingData>('hq/token-history-timing/data?page=' + pageNum)
            .then((result: AuthedJsonResult<TokenHistoryTimingData>) => {
                setLoading(false);
                switch (result.status) {
                    case 'success':
                        setError(null);
                        setData(result.data);
                        setTotalItems(result.data.totalItems ?? 0);
                        break;
                    case 'unauthenticated':
                        setError('Not signed in.');
                        break;
                    case 'network-error':
                        setError('Could not reach the server.');
                        break;
                    case 'api-error':
                        setError(result.message);
                        notify(result.message, { type: 'error' });
                        break;
                }
            });
    }

    function nextPage(): void {
        const currentPage: number = pageNum + 1;
        // Was `totalItems / 5` plus a conditional ++, which only rounded up when there was a
        // remainder — so on an exact multiple of the page size the last page was one short and
        // the guard below refused to open it, while the header still counted it.
        const maxPage: number = Math.ceil(totalItems / TIMINGS_PAGE_SIZE);
        if (currentPage <= maxPage) {
            setPageNum(currentPage);
        }
    }

    function previousPage(): void {
        const currentPage: number = pageNum - 1;
        if (currentPage >= 1) {
            setPageNum(currentPage);
        }
    }

    function reloadData(): void {
        setLoading(true);
        setError(null);
        loadData();
    }

    useEffect( () => {
        setLoading(true);
        loadData()
    }, [pageNum] );


    return { data, loading, error, totalItems, reloadData, nextPage, previousPage, pageNum }
}

export const useTokenCostData = () => {
    const [data, setData] = useState<TokenCostData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const notify = useNotify();

    function loadData(): void {
        fetchAuthedJson<TokenCostData>('hq/token-history-cost/data')
            .then((result: AuthedJsonResult<TokenCostData>) => {
                setLoading(false);
                switch (result.status) {
                    case 'success':
                        setError(null);
                        setData(result.data);
                        break;
                    case 'unauthenticated':
                        setError('Not signed in.');
                        break;
                    case 'network-error':
                        setError('Could not reach the server.');
                        break;
                    case 'api-error':
                        setError(result.message);
                        notify(result.message, { type: 'error' });
                        break;
                }
            });
    }

    const reloadData = () => {
        setLoading(true);
        setError(null);
        loadData();
        return true;
    }

    useEffect( () => {
        loadData();
    }, [] );

    return { data, loading, error, reloadData };
}
